import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS, ROLES, ZONA_HORARIA, hashPin, newId, verifyPin, type Role } from '@abastece/shared';
import type { Usuario } from '@abastece/core';
import { db, getMeta } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { emitir } from '../lib/emitir.js';
import { enServicio, equiposCallados, relojDesviado } from '../lib/equipos.js';
import { pinEnUso, problemaDelPin, quedaOtroDueno, soloDuenos, type ProblemaPin } from '../lib/personal.js';
import { enPractica } from '../lib/practica.js';
import { usePuede, useSesion } from '../lib/sesion.js';
import { ApiError, OfflineError, api } from '../sync/client.js';
import { sincronizar } from '../sync/engine.js';
import { localeActual, useT, type Clave } from '../i18n/index.js';
import { etiquetaRol } from '../i18n/dominio.js';
import { Campo, Selector } from '../ui/Campo.js';
import { Chip, Ico } from '../ui/Iconos.js';
import { Modal } from '../ui/Modal.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { Confirmar } from '../ui/Preguntar.js';

const MENSAJE_PIN: Record<ProblemaPin, Clave> = {
  corto: 'instalar.pinCorto',
  debil: 'instalar.pinDebil',
  noCoincide: 'instalar.pinNoCoincide',
};

/**
 * «Personal y equipos»: who signs in to the shop, and on which devices.
 *
 * Until this existed a shop could only ever have its owner: the one account the install made.
 * Everybody at the counter signed in as «Dueño», so the owner's PIN — asked for below the margin,
 * past a fiado limit, for a void from yesterday — was everybody's, and every void and every
 * shortfall was the owner's in the bitácora. A PIN that leaked could not be changed, somebody
 * who left could not be shut out, and a lost phone kept syncing.
 *
 * Every change is an event like any other (`usuario.*`, `dispositivo.revocado`, `dispositivo.reactivado`): it applies here
 * at once, reaches the other phones with the next sync, and goes in the bitácora under the owner
 * who made it. A PIN travels only as its hash.
 */
export function Personal() {
  const t = useT();
  const puede = usePuede('gestionar_usuarios');
  const sesion = useSesion();
  const personas = useLiveQuery(() => db.usuarios.toArray(), [], null);
  const [nueva, setNueva] = useState(false);
  const [editando, setEditando] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  if (!puede) {
    return (
      <Pagina titulo={t('personal.titulo')} volverA="/ajustes">
        <Vacio mensaje={t('comun.esDelDueno')} />
      </Pagina>
    );
  }
  if (!personas) return null;

  const ordenadas = [...personas].sort(
    (a, b) => Number(b.activo) - Number(a.activo) || a.nombre.localeCompare(b.nombre, 'es'),
  );
  const enEdicion = personas.find((p) => p.id === editando);

  return (
    <Pagina titulo={t('personal.titulo')} subtitulo={t('personal.subtitulo')} volverA="/ajustes">
      {aviso && (
        <p role="status" className="mb-4 rounded-xl bg-teal-500/10 px-4 py-3 text-sm text-teal-200">
          {aviso}
        </p>
      )}

      <div className="flex flex-col gap-4">
        <section className="tarjeta">
          <header className="mb-2 flex items-center gap-3">
            <Chip icono="personal" tinte="violeta" />
            <div className="min-w-0 flex-1">
              <h2 className="font-semibold">{t('personal.personas')}</h2>
              <p className="text-xs text-slate-500">{t('personal.personasAyuda')}</p>
            </div>
          </header>
          <ul className="flex flex-col divide-y divide-slate-800">
            {ordenadas.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  data-sin-icono="fila"
                  onClick={() => setEditando(p.id)}
                  className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-xl px-2 py-3 text-left transition hover:bg-white/5"
                >
                  <span className="min-w-0 flex-1">
                    <span className={`block break-words font-medium ${p.activo ? '' : 'text-slate-500 line-through'}`}>
                      {p.nombre}
                      {p.id === sesion?.usuarioId && (
                        <span className="ml-2 text-xs font-normal text-slate-400">{t('personal.usted')}</span>
                      )}
                    </span>
                    <span className="block text-sm text-slate-400">
                      {etiquetaRol(p.rol as Role)}
                      {!p.activo && <span className="ml-2 text-rose-300">{t('personal.desactivada')}</span>}
                    </span>
                  </span>
                  <Ico icono="adelante" className="size-5 shrink-0 text-slate-600" />
                </button>
              </li>
            ))}
          </ul>
          <button type="button" className="btn-secundario mt-3 w-full" onClick={() => setNueva(true)}>
            <Ico icono="contratar" />
            {t('personal.agregar')}
          </button>
        </section>

        <Equipos />
      </div>

      {nueva && (
        <NuevaPersona
          personas={personas}
          onCerrar={() => setNueva(false)}
          onAgregada={(texto) => {
            setNueva(false);
            setAviso(texto);
          }}
        />
      )}
      {enEdicion && (
        <EditarPersona
          persona={enEdicion}
          personas={personas}
          esUsted={enEdicion.id === sesion?.usuarioId}
          onCerrar={() => setEditando(null)}
        />
      )}
    </Pagina>
  );
}

/**
 * Somebody new: a name, what they do, and a PIN the owner gives them.
 *
 * The first person who is not an owner is also when voids and refunds start needing the owner:
 * a family shop has no need of it, and a shop with a hired cashier does. It is switched on here,
 * said so, and can be switched off again in «Configuración».
 */
function NuevaPersona({
  personas,
  onCerrar,
  onAgregada,
}: {
  personas: readonly Usuario[];
  onCerrar: () => void;
  onAgregada: (aviso: string) => void;
}) {
  const t = useT();
  const [nombre, setNombre] = useState('');
  const [rol, setRol] = useState<Role>('cajero');
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const problema = problemaDelPin(pin, pin2);
  const listo = nombre.trim() !== '' && pin !== '' && pin2 === pin && problema === null && !ocupado;

  async function agregar() {
    setOcupado(true);
    setError(null);
    try {
      if (await pinEnUso(pin, personas)) {
        setError(t('personal.pinEnUso'));
        return;
      }
      const limpio = nombre.trim().replace(/\s+/g, ' ');
      await emitir('usuario.creado', { usuarioId: newId(), nombre: limpio, rol, pinHash: await hashPin(pin) });
      const config = await readSettings();
      if (rol !== 'dueno' && soloDuenos(personas) && !config.anularConDueno) {
        await emitir('config.cambiada', { clave: 'anularConDueno', valor: true, anterior: false });
        onAgregada(t('personal.agregadaConDueno', { nombre: limpio }));
      } else {
        onAgregada(t('personal.agregada', { nombre: limpio }));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('personal.noSeGuardo'));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Modal
      titulo={t('personal.agregar')}
      onCerrar={onCerrar}
      pie={
        <button type="button" className="btn-primario w-full" disabled={!listo} onClick={() => void agregar()}>
          <Ico icono="contratar" />
          {t(ocupado ? 'comun.guardando' : 'comun.agregar')}
        </button>
      }
    >
      <div className="flex flex-col gap-4">
        <Campo
          etiqueta={t('comun.nombre')}
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          maxLength={80}
          autoFocus
          autoComplete="off"
        />
        <Selector
          etiqueta={t('personal.rol')}
          ayuda={t(`personal.rolAyuda.${rol}` as Clave)}
          value={rol}
          onChange={(e) => setRol(e.target.value as Role)}
        >
          {ROLES_EN_ORDEN.map((r) => (
            <option key={r} value={r}>
              {etiquetaRol(r)}
            </option>
          ))}
        </Selector>
        <ElegirPin pin={pin} pin2={pin2} setPin={setPin} setPin2={setPin2} problema={problema} />
        {error && <p className="text-sm text-rose-400">{error}</p>}
      </div>
    </Modal>
  );
}

/** Cashier first: that is who the owner is adding, nearly every time. */
const ROLES_EN_ORDEN: readonly Role[] = ['cajero', 'repartidor', ...ROLES.filter((r) => r !== 'cajero' && r !== 'repartidor')];

/**
 * One person: their name and what they do, a new PIN when they forget theirs or it got out, and
 * shutting them out when they leave.
 *
 * Deactivating keeps everything they did in their name, and stops them signing in on every
 * device with the next sync. What would leave the shop without an active owner is not offered,
 * and neither is the owner shutting themselves out: somebody else must be able to put it right.
 */
function EditarPersona({
  persona,
  personas,
  esUsted,
  onCerrar,
}: {
  persona: Usuario;
  personas: readonly Usuario[];
  esUsted: boolean;
  onCerrar: () => void;
}) {
  const t = useT();
  const [nombre, setNombre] = useState(persona.nombre);
  const [rol, setRol] = useState<Role>(persona.rol as Role);
  const [vista, setVista] = useState<'datos' | 'pin' | 'desactivar'>('datos');
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const ultimoDueno = persona.rol === 'dueno' && persona.activo && !quedaOtroDueno(personas, persona.id);
  // Your own role is not yours to change: an owner who made themselves a cashier could not undo it.
  const rolFijo = esUsted || ultimoDueno;
  const limpio = nombre.trim().replace(/\s+/g, ' ');
  const cambios = {
    ...(limpio && limpio !== persona.nombre ? { nombre: limpio } : {}),
    ...(rol !== persona.rol ? { rol } : {}),
  };
  const hayCambios = Object.keys(cambios).length > 0;
  const problema = problemaDelPin(pin, pin2);

  async function guardar() {
    setOcupado(true);
    setError(null);
    try {
      await emitir('usuario.actualizado', { usuarioId: persona.id, ...cambios });
      onCerrar();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('personal.noSeGuardo'));
    } finally {
      setOcupado(false);
    }
  }

  async function ponerPin() {
    setOcupado(true);
    setError(null);
    try {
      if (await pinEnUso(pin, personas, persona.id)) {
        setError(t('personal.pinEnUso'));
        return;
      }
      await emitir('usuario.pin_cambiado', { usuarioId: persona.id, pinHash: await hashPin(pin) });
      onCerrar();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('personal.noSeGuardo'));
    } finally {
      setOcupado(false);
    }
  }

  if (vista === 'desactivar') {
    return (
      <Confirmar
        titulo={t('personal.desactivarTitulo', { nombre: persona.nombre })}
        mensaje={t('personal.desactivarMensaje', { nombre: persona.nombre })}
        confirmar={t('personal.desactivar')}
        icono="desactivar"
        peligro
        onCerrar={() => setVista('datos')}
        onConfirmar={() => void emitir('usuario.desactivado', { usuarioId: persona.id }).then(onCerrar)}
      />
    );
  }

  if (vista === 'pin') {
    return (
      <Modal
        titulo={t('personal.pinNuevoDe', { nombre: persona.nombre })}
        onCerrar={onCerrar}
        pie={
          <div className="flex gap-3">
            <button type="button" className="btn-secundario flex-1" onClick={() => setVista('datos')}>
              <Ico icono="atras" />
              {t('comun.volver')}
            </button>
            <button
              type="button"
              className="btn-primario flex-1"
              disabled={!pin || pin2 !== pin || problema !== null || ocupado}
              onClick={() => void ponerPin()}
            >
              <Ico icono="guardar" />
              {t(ocupado ? 'comun.guardando' : 'comun.guardar')}
            </button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-slate-400">{t('personal.pinNuevoAyuda', { nombre: persona.nombre })}</p>
          <ElegirPin pin={pin} pin2={pin2} setPin={setPin} setPin2={setPin2} problema={problema} autoFocus />
          {error && <p className="text-sm text-rose-400">{error}</p>}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      titulo={persona.nombre}
      onCerrar={onCerrar}
      pie={
        <button
          type="button"
          className="btn-primario w-full"
          disabled={!hayCambios || !limpio || ocupado}
          onClick={() => void guardar()}
        >
          <Ico icono="guardar" />
          {t(ocupado ? 'comun.guardando' : 'comun.guardar')}
        </button>
      }
    >
      <div className="flex flex-col gap-4">
        <Campo
          etiqueta={t('comun.nombre')}
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          maxLength={80}
          autoComplete="off"
        />
        <Selector
          etiqueta={t('personal.rol')}
          ayuda={
            esUsted
              ? t('personal.rolPropio')
              : ultimoDueno
                ? t('personal.ultimoDueno')
                : t(`personal.rolAyuda.${rol}` as Clave)
          }
          value={rol}
          onChange={(e) => setRol(e.target.value as Role)}
          disabled={rolFijo}
          className={rolFijo ? 'text-slate-400' : ''}
        >
          {ROLES_EN_ORDEN.map((r) => (
            <option key={r} value={r}>
              {etiquetaRol(r)}
            </option>
          ))}
        </Selector>
        {error && <p className="text-sm text-rose-400">{error}</p>}

        <div className="flex flex-col gap-2 border-t border-slate-800 pt-4">
          {persona.activo && !esUsted && (
            <button type="button" className="btn-secundario w-full" onClick={() => setVista('pin')}>
              <Ico icono="entrar" />
              {t('personal.pinNuevo')}
            </button>
          )}
          {persona.activo && !esUsted && !ultimoDueno && (
            <button type="button" className="btn-secundario w-full text-rose-300" onClick={() => setVista('desactivar')}>
              <Ico icono="desactivar" />
              {t('personal.desactivar')}
            </button>
          )}
          {!persona.activo && (
            <button
              type="button"
              className="btn-secundario w-full"
              onClick={() => void emitir('usuario.reactivado', { usuarioId: persona.id }).then(onCerrar)}
            >
              <Ico icono="reiniciar" />
              {t('personal.reactivar')}
            </button>
          )}
          {esUsted && <p className="text-xs text-slate-500">{t('personal.ustedAyuda')}</p>}
        </div>
      </div>
    </Modal>
  );
}

/** A PIN and the same PIN again, said wrong as soon as it can be. */
function ElegirPin({
  pin,
  pin2,
  setPin,
  setPin2,
  problema,
  etiqueta,
  autoFocus,
}: {
  pin: string;
  pin2: string;
  setPin: (v: string) => void;
  setPin2: (v: string) => void;
  problema: ProblemaPin | null;
  etiqueta?: string;
  autoFocus?: boolean;
}) {
  const t = useT();
  return (
    <>
      <Campo
        etiqueta={etiqueta ?? t('instalar.pin')}
        type="password"
        inputMode="numeric"
        autoComplete="new-password"
        className="tabular"
        value={pin}
        onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
        maxLength={10}
        autoFocus={autoFocus}
        error={problema && problema !== 'noCoincide' ? t(MENSAJE_PIN[problema]) : null}
      />
      <Campo
        etiqueta={t('instalar.repitaPin')}
        type="password"
        inputMode="numeric"
        autoComplete="new-password"
        className="tabular"
        value={pin2}
        onChange={(e) => setPin2(e.target.value.replace(/\D/g, ''))}
        maxLength={10}
        error={problema === 'noCoincide' ? t(MENSAJE_PIN.noCoincide) : null}
      />
    </>
  );
}

/**
 * Your own PIN, from Ajustes, for anybody signed in: the one they were given, or one that got
 * out. The one they have now first, so a phone left signed in on the counter cannot be used to
 * take their account.
 */
export function CambiarMiPin({ onCerrar }: { onCerrar: () => void }) {
  const t = useT();
  const sesion = useSesion();
  const [actual, setActual] = useState('');
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [listo, setListo] = useState(false);

  const problema = problemaDelPin(pin, pin2);

  async function cambiar() {
    if (!sesion) return;
    setOcupado(true);
    setError(null);
    try {
      const personas = await db.usuarios.toArray();
      const yo = personas.find((p) => p.id === sesion.usuarioId);
      if (!yo || !(await verifyPin(actual, yo.pinHash))) {
        setError(t('pin.incorrecto'));
        return;
      }
      if (pin === actual || (await pinEnUso(pin, personas, yo.id))) {
        setError(t(pin === actual ? 'personal.pinIgual' : 'personal.pinEnUso'));
        return;
      }
      await emitir('usuario.pin_cambiado', { usuarioId: yo.id, pinHash: await hashPin(pin) });
      setListo(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('personal.noSeGuardo'));
    } finally {
      setOcupado(false);
    }
  }

  if (listo) {
    return (
      <Modal
        titulo={t('personal.miPin')}
        onCerrar={onCerrar}
        pie={
          <button type="button" className="btn-primario w-full" onClick={onCerrar}>
            <Ico icono="listo" />
            {t('comun.listo')}
          </button>
        }
      >
        <p className="text-sm text-slate-300">{t('personal.miPinListo')}</p>
      </Modal>
    );
  }

  return (
    <Modal
      titulo={t('personal.miPin')}
      onCerrar={onCerrar}
      pie={
        <button
          type="button"
          className="btn-primario w-full"
          disabled={!actual || !pin || pin2 !== pin || problema !== null || ocupado}
          onClick={() => void cambiar()}
        >
          <Ico icono="guardar" />
          {t(ocupado ? 'comun.guardando' : 'personal.cambiarPin')}
        </button>
      }
    >
      <div className="flex flex-col gap-4">
        <Campo
          etiqueta={t('personal.pinActual')}
          type="password"
          inputMode="numeric"
          autoComplete="current-password"
          className="tabular"
          value={actual}
          onChange={(e) => setActual(e.target.value.replace(/\D/g, ''))}
          maxLength={10}
          autoFocus
        />
        <ElegirPin
          pin={pin}
          pin2={pin2}
          setPin={setPin}
          setPin2={setPin2}
          problema={problema}
          etiqueta={t('personal.pinNuevoCampo')}
        />
        {error && <p className="text-sm text-rose-400">{error}</p>}
      </div>
    </Modal>
  );
}

type Equipo = {
  id: string;
  codigo: string;
  nombre: string;
  tipo: string;
  activo: boolean;
  ultimoVistoEn: string | null;
  relojDerivaMs?: number | null;
};

/**
 * The devices the shop registered, and when each one last reached the server — which only the
 * server knows, so this asks it, and says so when it cannot. A new one joins from its own
 * screen, on the owner's PIN; a lost one is revoked here, and is turned away the next time it
 * tries to sync. What it had not sent by then stays on it. Given back here (the owner,
 * 2026-10-06: «can we add something to unrevoke devices»), it syncs again with all of that, on its
 * own token: no wiping it and registering it again.
 */
function Equipos() {
  const t = useT();
  const locales = useLiveQuery(() => db.dispositivos.toArray(), [], []);
  const [remotos, setRemotos] = useState<Equipo[] | null>(null);
  const [sinServidor, setSinServidor] = useState(false);
  const [esteEquipo, setEsteEquipo] = useState<string | null>(null);
  const [dandoDeBaja, setDandoDeBaja] = useState<Equipo | null>(null);
  const [dandoDeAlta, setDandoDeAlta] = useState<Equipo | null>(null);
  const [vuelta, setVuelta] = useState(0);
  const practica = enPractica();
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);

  useEffect(() => {
    void getMeta('dispositivoId').then((id) => setEsteEquipo(id ?? null));
  }, []);

  useEffect(() => {
    if (practica) return;
    let vivo = true;
    api
      .dispositivos()
      .then((r) => {
        if (!vivo) return;
        setRemotos(r.dispositivos);
        setSinServidor(false);
      })
      .catch((e: unknown) => {
        if (vivo && (e instanceof OfflineError || e instanceof ApiError)) setSinServidor(true);
      });
    return () => {
      vivo = false;
    };
  }, [practica, vuelta]);

  /** The devices this one revoked or gave back and has not told the server yet. */
  const sinEnviar = useLiveQuery(
    async () =>
      new Set(
        (await db.eventos.where('estado').equals('pendiente').toArray())
          .filter((e) => e.type === 'dispositivo.revocado' || e.type === 'dispositivo.reactivado')
          .map((e) => (e.payload as { dispositivoId: string }).dispositivoId),
      ),
    [],
    new Set<string>(),
  );

  // The server's word once it answers, this phone's until then and for what it has not sent (`enServicio`).
  const porId = new Map(locales.map((d) => [d.id, d]));
  const equipos: Equipo[] = (remotos ?? locales.map((d) => ({ ...d, ultimoVistoEn: null }))).map((d) => ({
    ...d,
    activo: enServicio(remotos ? d.activo : null, porId.get(d.id)?.activo, sinEnviar.has(d.id)),
  }));
  const ordenados = [...equipos].sort((a, b) => Number(b.activo) - Number(a.activo) || a.codigo.localeCompare(b.codigo));
  /** Quiet for longer than the shop allows: what the home screen's alert is about. */
  const callados = new Set(equiposCallados(equipos, config.equipoCalladoHoras, esteEquipo, Date.now()).map((d) => d.id));

  async function darDeBaja(equipo: Equipo) {
    setDandoDeBaja(null);
    await emitir('dispositivo.revocado', { dispositivoId: equipo.id });
    await sincronizar();
    setVuelta((v) => v + 1);
  }

  async function darDeAlta(equipo: Equipo) {
    setDandoDeAlta(null);
    await emitir('dispositivo.reactivado', { dispositivoId: equipo.id });
    await sincronizar();
    setVuelta((v) => v + 1);
  }

  return (
    <section className="tarjeta">
      <header className="mb-2 flex items-center gap-3">
        <Chip icono="telefono" tinte="cielo" />
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold">{t('personal.equipos')}</h2>
          <p className="text-xs text-slate-500">{t('personal.equiposAyuda')}</p>
        </div>
      </header>
      {(practica || sinServidor) && (
        <p className="mb-2 text-xs text-amber-300">
          {t(practica ? 'personal.equiposPractica' : 'personal.equiposSinServidor')}
        </p>
      )}
      <ul className="flex flex-col divide-y divide-slate-800">
        {ordenados.map((d) => (
          // Under 360 px the button goes under the name: beside it, «Dar de baja» or «Dar de alta»
          // left the name some 80 px, and «Desarrollo» broke into three pieces.
          <li key={d.id} className="flex items-center gap-3 py-3 max-[359px]:flex-wrap max-[359px]:gap-y-1" data-equipo={d.id} data-activo={d.activo}>
            <span
              className={`tabular flex size-10 shrink-0 items-center justify-center rounded-lg text-sm font-semibold ring-1 ring-inset ${
                d.activo ? 'bg-slate-800 ring-slate-700' : 'text-slate-500 ring-slate-800'
              }`}
            >
              {d.codigo}
            </span>
            <span className="min-w-0 flex-1 max-[359px]:basis-[calc(100%-3.25rem)]">
              <span className={`block break-words font-medium ${d.activo ? '' : 'text-slate-500 line-through'}`}>
                {d.nombre}
                {d.id === esteEquipo && (
                  <span className="ml-2 text-xs font-normal text-slate-400">{t('personal.esteEquipo')}</span>
                )}
              </span>
              <span className={`block text-xs ${callados.has(d.id) ? 'text-amber-300' : 'text-slate-500'}`}>
                {t(`personal.tipo.${d.tipo}` as Clave)}
                {' · '}
                {!d.activo
                  ? t('personal.dadoDeBaja')
                  : d.ultimoVistoEn
                    ? t('personal.vistoEn', {
                        cuando: new Date(d.ultimoVistoEn).toLocaleString(localeActual(), {
                          timeZone: ZONA_HORARIA,
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        }),
                      })
                    : remotos
                      ? t('personal.nuncaVisto')
                      : '—'}
              </span>
              {d.activo && relojDesviado(d.relojDerivaMs, config.relojDerivaMaxMs) && (
                <span className="block text-xs text-amber-300">
                  {t(d.relojDerivaMs > 0 ? 'personal.relojAdelantado' : 'personal.relojAtrasado', {
                    min: Math.round(Math.abs(d.relojDerivaMs) / 60_000),
                  })}
                </span>
              )}
            </span>
            {d.activo && d.id !== esteEquipo && !practica && (
              <button
                type="button"
                className="btn-texto shrink-0 gap-1 text-xs text-rose-300 max-[359px]:ml-13"
                onClick={() => setDandoDeBaja(d)}
                data-dar-de-baja
              >
                <Ico icono="desactivar" className="size-3.5" />
                {t('personal.darDeBaja')}
              </button>
            )}
            {!d.activo && d.id !== esteEquipo && !practica && (
              <button
                type="button"
                className="btn-texto shrink-0 gap-1 text-xs text-marca-300 max-[359px]:ml-13"
                onClick={() => setDandoDeAlta(d)}
                data-dar-de-alta
              >
                <Ico icono="reiniciar" className="size-3.5" />
                {t('personal.darDeAlta')}
              </button>
            )}
          </li>
        ))}
      </ul>
      {!practica && <p className="mt-2 text-xs text-slate-500">{t('personal.agregarEquipo')}</p>}

      {dandoDeBaja && (
        <Confirmar
          titulo={t('personal.darDeBajaTitulo', { nombre: dandoDeBaja.nombre })}
          mensaje={t('personal.darDeBajaMensaje')}
          confirmar={t('personal.darDeBaja')}
          icono="desactivar"
          peligro
          onCerrar={() => setDandoDeBaja(null)}
          onConfirmar={() => void darDeBaja(dandoDeBaja)}
        />
      )}
      {dandoDeAlta && (
        <Confirmar
          titulo={t('personal.darDeAltaTitulo', { nombre: dandoDeAlta.nombre })}
          mensaje={t('personal.darDeAltaMensaje')}
          confirmar={t('personal.darDeAlta')}
          icono="reiniciar"
          onCerrar={() => setDandoDeAlta(null)}
          onConfirmar={() => void darDeAlta(dandoDeAlta)}
        />
      )}
    </section>
  );
}
